import { Member, MemberBaseData, SettingsAccountInfo, MemberCustomerData, MemberAggregateData, TripData } from './Models/memberClasses';
import { getResourceData } from './resource';
import { get } from './Utilities/fetchUtilities';
import eventBus from './eventBus';
import cache from './cache';

declare let Promise: any;//Tricking ts linter from thinking Promise.all is bad

const baseMemberUrl = "/F9Sessionless/Member",
    aggregateUrl = "/F9Sessionless/Aggregate",
    customerUrl = "/F9Sessionless/Customer",
    tripsUrl = "/F9Sessionless/Trips",
    accountUrl = "F9Loyalty/Account",
    events = {
        memberLoadedEvent: "memberLoaded",
        baseMemberLoadedEvent: "baseMemberLoaded",
        aggregateLoadedEvent: "aggregateLoaded",
        customerLoadedEvent: "customerLoaded",
        tripsLoadedEvent: "tripsLoaded",
        dataStartedEvent: "dataStarted"
    };


export type memberEvents = "memberLoaded" | "baseMemberLoaded" | "aggregateLoaded" | "customerLoaded" | "dataStarted";

export function getMember(allowCached: Boolean = true, member: Member = new Member(), host: string = ""): Promise<Member> {
    if(!member) {
        member = new Member();
    }
    
    return Promise.all([
        getBaseMemberData(allowCached, host).then(x => member.memberData = x),
        getAggregateData(allowCached, host).then(x => member.aggregateData = x),
        getCustomerData(allowCached, host).then(x => member.customerData = x),
        getResourceData(allowCached, host).then(x => member.resourceData = x),
        getTripData(allowCached, host).then(x => member.tripData = x)
    ]).then(() => {
        eventBus.dispatch(events.memberLoadedEvent, {data: member});

        return member;
    });
}

export function getBaseMemberData(allowCached: Boolean = true, host: string = ""): Promise<MemberBaseData> {
    //This technique of caching will allow the data to be available to other vue instances on the window (ex: Loyalty & Header).  This will only be stored for the current page load.
    //This will prevent any timing problems because we are caching the Promise and not the result of the promise, which will always resolve even if it's been completed.
    const cacheName = "baseMemberPromise",
        cached = cache.get<Promise<MemberBaseData>>(cacheName),
        url = host + baseMemberUrl;
    
    if(!cached || !allowCached) {
        eventBus.dispatch(events.dataStartedEvent, { data: "memberData" });

        return cache.set(cacheName, get(url)
            .then(response => response.json()).then(json => {
                let data = new MemberBaseData(json);

                eventBus.dispatch(events.baseMemberLoadedEvent, {data});

                return data;
            }));
    }
    
    return cached;
}

export function getSettingsAccountInfo(allowCached: Boolean = true, host: string = ""): Promise<SettingsAccountInfo> {
    //This technique of caching will allow the data to be available to other vue instances on the window (ex: Loyalty & Header).  This will only be stored for the current page load.
    //This will prevent any timing problems because we are caching the Promise and not the result of the promise, which will always resolve even if it's been completed.
    const cacheName = "accountInfoPromise",
        cached = cache.get<Promise<SettingsAccountInfo>>(cacheName),
        url = host + accountUrl;

    if (!cached || !allowCached) {
        eventBus.dispatch(events.dataStartedEvent, { data: "settingsAccount" });

        return cache.set(cacheName, get(url)
            .then(response => response.json()).then(json => {
                let data = new SettingsAccountInfo(json);
                return data;
            }));
    }

    return cached;
}

export function getAggregateData(allowCached: Boolean = true, host: string = ""): Promise<MemberAggregateData> {
    const cacheName = "aggregatePromise",
        cached = cache.get<Promise<MemberAggregateData>>(cacheName),
        url = host + aggregateUrl;

    if(!cached || !allowCached) {
        eventBus.dispatch(events.dataStartedEvent, { data: "aggregateData" });

        return cache.set(cacheName, get(url)
            .then(response => response.json())
            .then(json => {
                let data = new MemberAggregateData(json);

                eventBus.dispatch(events.aggregateLoadedEvent, {data});

                return data;
            }));
    }
    
    return cached;
}

export function getCustomerData(allowCached: Boolean = true, host: string = ""): Promise<MemberCustomerData> {
    const cacheName = "customerPromise",
        cached = cache.get<Promise<MemberCustomerData>>(cacheName),
        url = host + customerUrl;

    if(!cached || !allowCached) {
        eventBus.dispatch(events.dataStartedEvent, { data: "customerData" });

        return cache.set(cacheName, get(url)
            .then(response => response.json())
            .then(json => {
                let data = new MemberCustomerData(json);

                eventBus.dispatch(events.customerLoadedEvent, {data});

                return data;
            }));
    }

    return cached;
}

export function getTripData(allowCached: Boolean = true, host: string = ""): Promise<TripData> {
    const cacheName = "tripPromise",
        cached = cache.get<Promise<TripData>>(cacheName),
        url = host + tripsUrl;
    const currentUrl = window.location.pathname;
    var isProfilePage = false;
    if (currentUrl.includes("/FrontierMiles/Profile") || currentUrl.includes("/Member/Profile")) {
        isProfilePage = true;
    }
        if(!cached || !allowCached) {
            eventBus.dispatch(events.dataStartedEvent, { data: "tripData" });
            
            return cache.set(cacheName, get(url + `?isProfilePage=${isProfilePage}`).then(response => response.json())
            .then(json => {
                let data = new TripData(json);

                eventBus.dispatch(events.tripsLoadedEvent, {data});

                return data;
            }));
        }
    return cached;
}

export function attachToMemberEvent (event: memberEvents, func: EventListenerOrEventListenerObject): void {
    eventBus.attach(event, func);
}

export function detachFromMemberEvent (event: memberEvents, func:  EventListenerOrEventListenerObject): void {
    eventBus.detach(event, func);
}